/**
 * The builder as one component: palette, canvas, inspector, and the bar above
 * them.
 *
 * It owns no saving. The screen around it - the campaign editor, the template
 * editor - holds the letter and decides when it is written down; this only
 * reports every edit through `onChange`. Two save buttons on one screen is how
 * a letter ends up stored with a design that no longer matches its HTML.
 */
import { useCallback, useState } from "react";
import { Monitor, Redo2, Smartphone, Undo2 } from "lucide-react";
import { Button, cn } from "@wa/ui";
import { useT } from "../i18n/I18nProvider";
import { absoluteMediaUrl } from "../lib/media";
import { MediaPicker } from "../components/MediaPicker";
import { Canvas } from "./canvas/Canvas";
import { Inspector } from "./inspector/Inspector";
import { Palette } from "./Palette";
import { BuilderProvider, useBuilder } from "./store";
import type { EmailDesign } from "./types";

export function EmailBuilder({
  design,
  onChange,
  disabled,
  siteId,
}: {
  design: EmailDesign | null;
  onChange: (design: EmailDesign) => void;
  disabled?: boolean;
  /** The host site whose media library the image blocks pick from. Absent means
   *  no picker - the URL field still works, so the builder stays usable. */
  siteId?: string | null;
}) {
  return (
    <BuilderProvider design={design} onChange={onChange} disabled={disabled}>
      <BuilderShell siteId={siteId} />
    </BuilderProvider>
  );
}

/**
 * A media-library URL an inbox can actually fetch.
 *
 * Re-exported rather than defined here: the media screen needs the same rule
 * for the link it puts on the clipboard, and importing it out of the builder
 * would drag the whole editor into that route's bundle. The rule now lives with
 * the client that produces these URLs (`lib/media`); this name stays so the
 * builder's own public surface does not move. The inspector still says so when
 * a src is left relative.
 */
export { absoluteMediaUrl };

function BuilderShell({ siteId }: { siteId?: string | null }) {
  const t = useT();
  const { dispatch, canUndo, canRedo, disabled } = useBuilder();
  const [phone, setPhone] = useState(false);
  // The picker is opened from a block's own field, so the callback that
  // receives the chosen URL travels with the request rather than being wired
  // through the tree.
  const [pick, setPick] = useState<((url: string) => void) | null>(null);

  const onPickImage = useCallback(
    (apply: (url: string) => void) => setPick(() => apply),
    [],
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card/70 shadow-soft backdrop-blur">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            title={t("marketing.builder.undo")}
            disabled={!canUndo || disabled}
            onClick={() => dispatch({ kind: "undo" })}
          >
            <Undo2 className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title={t("marketing.builder.redo")}
            disabled={!canRedo || disabled}
            onClick={() => dispatch({ kind: "redo" })}
          >
            <Redo2 className="size-4" />
          </Button>
        </div>

        {/* Width only. Not a phone frame: the letter is the same HTML at both
            widths, and drawing a bezel around it would suggest the builder
            knows how a phone's mail app will treat it, which it does not. */}
        <div className="inline-flex rounded-md border border-border p-0.5">
          <button
            type="button"
            onClick={() => setPhone(false)}
            title={t("marketing.builder.widthDesktop")}
            className={cn(
              "rounded px-2 py-1 transition-colors",
              !phone ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
            )}
          >
            <Monitor className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setPhone(true)}
            title={t("marketing.builder.widthPhone")}
            className={cn(
              "rounded px-2 py-1 transition-colors",
              phone ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
            )}
          >
            <Smartphone className="size-3.5" />
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-[13rem_minmax(0,1fr)_17rem]">
        <aside className="max-h-[34rem] overflow-y-auto border-b border-border p-3 lg:max-h-[46rem] lg:border-b-0 lg:border-r">
          <Palette />
        </aside>

        <div className={cn("min-w-0", phone && "flex justify-center")}>
          <div
            className={cn("max-h-[46rem] min-h-[24rem] w-full overflow-hidden", phone && "max-w-[380px] border-x border-border")}
          >
            <Canvas />
          </div>
        </div>

        <aside className="max-h-[46rem] overflow-y-auto border-t border-border p-3 lg:border-l lg:border-t-0">
          <Inspector onPickImage={onPickImage} />
        </aside>
      </div>

      {siteId && (
        <MediaPicker
          open={pick !== null}
          siteId={siteId}
          onClose={() => setPick(null)}
          onPick={(asset) => {
            pick?.(absoluteMediaUrl(asset.url));
            setPick(null);
          }}
        />
      )}
    </div>
  );
}
